@file:Suppress("MagicNumber", "ReturnCount")

package com.nonopn.skywatcher.maps

import com.nonopn.skywatcher.maps.MapUtils.toRadian
import kotlinx.serialization.Serializable
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.exp
import kotlin.math.ln
import kotlin.math.max
import kotlin.math.sin
import kotlin.math.sqrt

// Approximation locale : conversion lat/lon → mètres (proche de l'équateur)
fun latLonToLocalXY(position0: LatLng, position1: LatLng): Pair<Double, Double> {
    val earthRadius = 6378137.0 // rayon équatorial en mètres
    val dLat = toRadian(position1.latitude - position0.latitude)
    val dLon = toRadian(position1.longitude - position0.longitude)
    val avgLat = toRadian((position1.latitude + position0.latitude) / 2)

    val dx = dLon * earthRadius * cos(avgLat)
    val dy = dLat * earthRadius
    return Pair(dx, dy)
}

fun mahalanobisDistanceSquared(
    dx: Double,
    dy: Double,
    a: Double,
    b: Double,
    thetaDeg: Double
): Double {
    val theta = toRadian(thetaDeg)
    val cosT = cos(theta)
    val sinT = sin(theta)

    // Rotation inverse du vecteur (dx, dy)
    val xRot = cosT * dx + sinT * dy
    val yRot = -sinT * dx + cosT * dy

    // Calcul de la distance au carré, normalisée par les axes
    return (xRot * xRot) / (a * a) + (yRot * yRot) / (b * b)
}

@Serializable
data class ProbabilityHolder(
    val distance: Double,
    val imprecise: Double,
    val preciseProbability: Double
)

fun testMahalanobisKMP(
    position1: LatLng, // Centre de l'ellipse
    position2: LatLng, // Point à tester
    a: Double,
    b: Double, // Demi-axes de l'ellipse (en mètres)
    thetaDeg: Double, // Angle de rotation
    // threshold: Double = 4000.0,
    // penaltyScale: Double = 100.0,
    thresholdPenaltyDistance: Double = 600.0,
    penaltyDistanceScale: Double = 100.0
): ProbabilityHolder {
    // println("testMahalanobisKMP")
    // println("  P1=$position1")
    // println("  P2=$position2")
    // println("  a=$a")
    // println("  b=$b")
    // println("  thetaDeg=$thetaDeg")
    val distanceGps = MapUtils.distance(position1, position2) * 1000
    val (dx, dy) = latLonToLocalXY(position1, position2)
    val d2 = mahalanobisDistanceSquared(dx, dy, a, b, thetaDeg)
    val distance = sqrt(d2)
    val pValue = chiSquaredSurvival2Precise(d2) // chiSquaredSurvival2(d2)
    // val penalty = uncertaintyPenaltyExpSmooth(a, b, threshold, penaltyScale)
    val penaltyDistance =
        penaltyFromDistance(distanceGps, thresholdPenaltyDistance, penaltyDistanceScale)
    return ProbabilityHolder(
        distance = distance,
        imprecise = pValue,
        preciseProbability = pValue * penaltyDistance // * penalty
    )
}

/**
 * Penalties depending on the a / b axes
 */
fun uncertaintyPenaltyExpSmooth(
    a: Double,
    b: Double,
    threshold: Double = 3000.0,
    scale: Double = 1000.0
): Double {
    // println("$a $b ${threshold} $scale")
    val norm = sqrt(a * a + b * b)
    val excess = max(0.0, norm - threshold)
    return exp(-excess / scale)
}

// penalty depending on the distance
fun penaltyFromDistance(
    distanceMeters: Double,
    threshold: Double = 1000.0,
    scale: Double = 3000.0
): Double {
    val excess = max(0.0, distanceMeters - threshold)
    // println("penalty from distance $distanceMeters $threshold $scale -> $excess -> ${exp(-excess / scale)}")
    return exp(-excess / scale)
}

/**
 * Calcul de ln(Gamma(s)) via approximation de Lanczos
 */
fun logGamma(x: Double): Double {
    val coeffs = doubleArrayOf(
        76.18009172947146,
        -86.50532032941678,
        24.01409824083091,
        -1.231739572450155,
        0.001208650973866179,
        -0.000005395239384953
    )
    var y = x
    var tmp = x + 5.5
    tmp -= (x + 0.5) * ln(tmp)
    var ser = 1.000000000190015
    for (j in coeffs.indices) {
        y += 1.0
        ser += coeffs[j] / y
    }
    return -tmp + ln(2.5066282746310005 * ser / x)
}

// Gamma(s)
fun gamma(s: Double): Double = exp(logGamma(s))

// Gamma incomplète régulière : P(s, x) = γ(s, x) / Γ(s)
fun regularizedGammaP(
    s: Double,
    x: Double,
    epsilon: Double = 1e-10,
    maxIterations: Int = 1000
): Double {
    if (x < 0.0 || s <= 0.0) return Double.NaN
    if (x == 0.0) return 0.0
    if (x < s + 1.0) {
        // Série (gamma inférieure)
        var sum = 1.0 / s
        var term = 1.0 / s
        for (n in 1..maxIterations) {
            term *= x / (s + n)
            sum += term
            if (term < sum * epsilon) break
        }
        return sum * exp(-x + s * ln(x) - logGamma(s))
    } else {
        // Fraction continue (gamma supérieure)
        var a = 1.0 - s
        var b = a + x + 1.0
        var c = 1.0 / 1.0e-30
        var d = 1.0 / b
        var h = d
        for (n in 1..maxIterations) {
            val an = n * (s - n)
            a += 2.0
            b += 2.0
            d = an * d + b
            if (abs(d) < 1e-30) d = 1e-30
            c = b + an / c
            if (abs(c) < 1e-30) c = 1e-30
            d = 1.0 / d
            val delta = d * c
            h *= delta
            if (abs(delta - 1.0) < epsilon) break
        }
        return 1.0 - h * exp(-x + s * ln(x) - logGamma(s))
    }
}

// CDF from chi² law to 2 ddl
fun chiSquaredCDF2(x: Double): Double = regularizedGammaP(1.0, x / 2)

// Survival function p-value = 1 - CDF
fun chiSquaredSurvival2Precise(x: Double): Double = 1.0 - chiSquaredCDF2(x)
